import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { Button, Form, Input, SelectSearch } from "@telluscompany/design-system";
import { Filter } from "lucide-react";

import { useLocalization } from "@i18n";
import { cleanEmptyStrings } from "@shared/hooks";

import type {
  DamRaisingMethod,
  GeneralRaisingHistoryFilters,
} from "../../api/generalRaisingHistory/generalRaisingHistory.types";

export type GeneralRaisingHistoryFilterData = {
  stage: string;
  method?: DamRaisingMethod;
};

const DAM_RAISING_METHODS: DamRaisingMethod[] = [
  "downstream",
  "upstream",
  "centerLine",
];

const EMPTY_FORM: GeneralRaisingHistoryFilterData = {
  stage: "",
  method: undefined,
};

export type GeneralRaisingHistoryFilterProps = {
  onFilter: (filters: Pick<GeneralRaisingHistoryFilters, "stage" | "method">) => void;
  onClear: () => void;
  initialFilters?: Partial<GeneralRaisingHistoryFilterData>;
};

export const GeneralRaisingHistoryFilter = ({
  onFilter,
  onClear,
  initialFilters,
}: GeneralRaisingHistoryFilterProps) => {
  const { translate } = useLocalization();

  const form = useForm<GeneralRaisingHistoryFilterData>({
    defaultValues: {
      stage: initialFilters?.stage ?? "",
      method: initialFilters?.method,
    },
  });

  const methodOptions = useMemo(
    () =>
      DAM_RAISING_METHODS.map((value) => ({
        value,
        label: translate(`admin.structures.raisingHistory.method.${value}`),
      })),
    [translate],
  );

  const handleFilter = (data: GeneralRaisingHistoryFilterData) => {
    onFilter(
      cleanEmptyStrings(data) as Pick<
        GeneralRaisingHistoryFilters,
        "stage" | "method"
      >,
    );
  };

  const handleClear = () => {
    form.reset(EMPTY_FORM);
    onClear();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFilter)}>
        <div className="flex flex-column gap-5">
          <Input
            control={form.control}
            label={`${translate("admin.structures.raisingHistory.filter.label.stage")}:`}
            name="stage"
            type="text"
            data-testid="raising-history-filter-stage"
            placeholder={translate(
              "admin.structures.raisingHistory.filter.placeholder.stage",
            )}
          />
          <SelectSearch
            control={form.control}
            label={`${translate("admin.structures.raisingHistory.filter.label.method")}:`}
            name="method"
            placeholder={translate(
              "admin.structures.raisingHistory.filter.placeholder.method",
            )}
            options={methodOptions}
            clearable
          />
        </div>
        <div className="mb-4 flex justify-end gap-4">
          <Button type="button" variant="secondary" onClick={handleClear}>
            {translate(
              "admin.structures.raisingHistory.filter.button.clearFilters",
            )}
          </Button>
          <Button
            type="submit"
            data-testid="raising-history-filter-submit"
            iconLeft={<Filter fill="currentColor" />}
          >
            {translate("admin.structures.raisingHistory.table.filterButtonText")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
